import { type Scene } from '../scene/Scene';
import { type Behavior, type BehaviorType } from './Behavior';
import { type ComponentType } from './ComponentType';
import { type EntityId } from './EntityId';
import { Transform } from './Transform';
import { composeWorldTransform, WorldTransform } from './WorldTransform';

/**
 * A handle to one entity in a scene.
 *
 * The scene owns the registry; an entity is the convenient face of a row in it. Everything an entity
 * carries falls into one of four buckets: identity (`id`, `name`, tags), placement (`transform`,
 * parent and children), data (components) and logic (behaviors).
 *
 * Hold an {@link EntityId} rather than an `Entity` when referring to another entity across frames, so
 * a destroyed target surfaces as a failed lookup instead of a stale object.
 */
export class Entity {
  /** Stable identity within the owning scene. Never reused, even after this entity is destroyed. */
  readonly id: EntityId;
  /** The scene that owns this entity. */
  readonly scene: Scene;
  /** Placement relative to the parent, or to the world when there is no parent. */
  readonly transform = new Transform();
  /** Human readable label, used by the editor and by name lookups. Not unique and not an identity. */
  name: string;

  readonly #components = new Map<ComponentType, object>();
  readonly #behaviors: Behavior[] = [];
  readonly #tags = new Set<string>();
  readonly #children: Entity[] = [];

  #parent: Entity | undefined;
  #active = true;
  #destroyed = false;

  /** @internal Entities are created through {@link Scene.createEntity}. */
  constructor(scene: Scene, id: EntityId, name: string) {
    this.scene = scene;
    this.id = id;
    this.name = name;
  }

  /** Whether this entity has been destroyed. A destroyed entity must not be used again. */
  get destroyed(): boolean {
    return this.#destroyed;
  }

  /** Whether this entity itself is enabled, ignoring its ancestors. */
  get active(): boolean {
    return this.#active;
  }

  set active(value: boolean) {
    this.#active = value;
  }

  /**
   * Whether this entity and every ancestor are active.
   *
   * Systems test this rather than {@link Entity.active}, so disabling a parent hides its whole subtree.
   */
  get activeInHierarchy(): boolean {
    if (!this.#active) {
      return false;
    }
    return this.#parent?.activeInHierarchy ?? true;
  }

  /** The parent entity, or undefined when this entity sits at the root of the scene. */
  get parent(): Entity | undefined {
    return this.#parent;
  }

  /** Direct children, in the order they were parented. */
  get children(): readonly Entity[] {
    return this.#children;
  }

  /** Every component on this entity, for introspection. Do not mutate the array. */
  get components(): readonly object[] {
    return [...this.#components.values()];
  }

  /** Every behavior on this entity, in attach order, which is also update order. */
  get behaviors(): readonly Behavior[] {
    return this.#behaviors;
  }

  /** Tags on this entity. */
  get tags(): ReadonlySet<string> {
    return this.#tags;
  }

  /**
   * Reparents this entity, or moves it to the scene root when `parent` is undefined.
   *
   * The transform is interpreted relative to the new parent; world position is not preserved. Throws
   * when the requested parent would create a cycle or belongs to another scene.
   */
  setParent(parent: Entity | undefined): void {
    this.#assertUsable();
    if (parent === this.#parent) {
      return;
    }

    if (parent !== undefined) {
      if (parent.scene !== this.scene) {
        throw new Error(`Cannot parent "${this.name}" to an entity from a different scene.`);
      }
      for (let ancestor: Entity | undefined = parent; ancestor !== undefined; ancestor = ancestor.#parent) {
        if (ancestor === this) {
          throw new Error(`Cannot parent "${this.name}" to its own descendant "${parent.name}".`);
        }
      }
    }

    this.#removeFromParent();
    this.#parent = parent;
    if (parent !== undefined) {
      parent.#children.push(this);
    }
  }

  /**
   * Attaches a component and returns it, so it can be configured inline.
   *
   * One component per type per entity; adding a second of the same type throws rather than silently
   * replacing data another system may already be reading.
   */
  addComponent<TComponent extends object>(component: TComponent): TComponent {
    this.#assertUsable();
    const type = component.constructor as ComponentType<TComponent>;
    if (this.#components.has(type)) {
      throw new Error(`Entity "${this.name}" already has a ${type.name} component.`);
    }

    this.#components.set(type, component);
    this.scene.indexComponent(this, type);
    return component;
  }

  /**
   * The component of exactly this type, or undefined when the entity has none.
   *
   * Lookup is by exact class, not by base class, because components are data and the type is their
   * identity.
   */
  getComponent<TComponent extends object>(type: ComponentType<TComponent>): TComponent | undefined {
    return this.#components.get(type) as TComponent | undefined;
  }

  /** Like {@link Entity.getComponent} but throws when the component is missing. */
  requireComponent<TComponent extends object>(type: ComponentType<TComponent>): TComponent {
    const component = this.getComponent(type);
    if (component === undefined) {
      throw new Error(`Entity "${this.name}" has no ${type.name} component.`);
    }
    return component;
  }

  /** Whether a component of exactly this type is attached. */
  hasComponent(type: ComponentType): boolean {
    return this.#components.has(type);
  }

  /** Removes the component of this type and reports whether one was there. */
  removeComponent(type: ComponentType): boolean {
    if (!this.#components.delete(type)) {
      return false;
    }
    this.scene.unindexComponent(this, type);
    return true;
  }

  /**
   * Attaches a behavior, runs its `onAttach` hook and returns it.
   *
   * Behaviors update in attach order, so ordering between two behaviors on the same entity is a
   * deliberate choice made at construction time rather than an accident of iteration.
   */
  addBehavior<TBehavior extends Behavior>(behavior: TBehavior): TBehavior {
    this.#assertUsable();
    this.#behaviors.push(behavior);
    behavior.attach(this, this.scene.context);
    return behavior;
  }

  /**
   * The first behavior that is an instance of `type`, or undefined when there is none.
   *
   * Unlike components, behaviors match subclasses too: asking for a base behavior finds a derived one.
   */
  getBehavior<TBehavior extends Behavior>(type: BehaviorType<TBehavior>): TBehavior | undefined {
    return this.#behaviors.find((behavior) => behavior instanceof type) as TBehavior | undefined;
  }

  /** Detaches a behavior and reports whether it was attached to this entity. */
  removeBehavior(behavior: Behavior): boolean {
    const index = this.#behaviors.indexOf(behavior);
    if (index < 0) {
      return false;
    }
    this.#behaviors.splice(index, 1);
    behavior.detach();
    return true;
  }

  /** Adds a tag, used for cheap scene-wide lookups such as "every collectible". */
  addTag(tag: string): this {
    this.#assertUsable();
    if (!this.#tags.has(tag)) {
      this.#tags.add(tag);
      this.scene.indexTag(this, tag);
    }
    return this;
  }

  /** Whether this entity carries the given tag. */
  hasTag(tag: string): boolean {
    return this.#tags.has(tag);
  }

  /** Removes a tag and reports whether it was there. */
  removeTag(tag: string): boolean {
    if (!this.#tags.delete(tag)) {
      return false;
    }
    this.scene.unindexTag(this, tag);
    return true;
  }

  /**
   * Resolves this entity's absolute placement by folding in every ancestor transform.
   *
   * Pass an instance to fill to keep the render loop allocation free; a fresh one is created otherwise.
   */
  getWorldTransform(out: WorldTransform = new WorldTransform()): WorldTransform {
    const parent = this.#parent;
    if (parent === undefined) {
      return out.copyFromLocal(this.transform);
    }
    parent.getWorldTransform(out);
    composeWorldTransform(out, this.transform, out);
    return out;
  }

  /**
   * Destroys this entity and its whole subtree, immediately.
   *
   * Behaviors are detached, the entity leaves the scene registry and every later lookup of its id
   * returns undefined. Systems iterate over snapshots, so destroying an entity from inside a behavior
   * update is safe.
   */
  destroy(): void {
    if (this.#destroyed) {
      return;
    }
    this.#destroyed = true;

    for (const child of [...this.#children]) {
      child.destroy();
    }
    this.#children.length = 0;
    this.#removeFromParent();

    for (const behavior of [...this.#behaviors].reverse()) {
      behavior.detach();
    }
    this.#behaviors.length = 0;

    this.scene.releaseEntity(this);
    this.#components.clear();
    this.#tags.clear();
  }

  #removeFromParent(): void {
    const parent = this.#parent;
    if (parent === undefined) {
      return;
    }
    const index = parent.#children.indexOf(this);
    if (index >= 0) {
      parent.#children.splice(index, 1);
    }
    this.#parent = undefined;
  }

  #assertUsable(): void {
    if (this.#destroyed) {
      throw new Error(`Entity "${this.name}" has been destroyed.`);
    }
  }
}
